import { Button, EmptyState, Tabs, VSpacer } from '@/components/DesignSystem';
import ConfirmModal from '@/components/shared/ConfirmModal';
import { getTabBarSx, TabCardRadius } from '@/components/shared/styles/cardTabBar';
import { AppConfig } from '@/constants/AppConfig';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useGetRetailerById, useGetRetailerMembers } from '@/hooks/useRetailer';
import { useSearch } from '@/hooks/useSearch';
import {
  AddRecipientsSection,
} from '@/pages/Admin/HierarchyOfRetailers/Recipients/AddRecipientsSection';
import {
  BrandAssetInputs,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/BrandAssetInputs';
import {
  ProgramSectionHeader,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/ProgramSectionHeader';
import {
  RewardsProgramsCard,
// eslint-disable-next-line max-len
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/RewardsProgramsCard/RewardsProgramsCard';
import {
  HistoricalCutoffCard,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/HistoricalCutoffCard';
import {
  EnergyProgramCard,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/EnergyProgramCard';
import {
  getMostRelevantProgram,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/helpers';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { JobApi } from '@/utilities/api/JobApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RetailerEndpoint } from '@api/endpoints';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import {
  Box, CircularProgress, Container, Stack, Tab, useTheme,
} from '@mui/material';
import { RewardsProgramType } from '@shared/enums/RewardsProgramType';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ErpImports } from './Imports/ErpImports';
import { IntegrationDetails } from './Integrations/IntegrationDetails';
import LocationCard from './Locations/LocationOverviewSection';
import MembersCard from './Members/MemberOverviewSection';
import RetailerDetailsCard from './RetailerDetailsCard';
import {
  CategoryMappingCard,
} from '@/pages/Admin/HierarchyOfRetailers/CategoryMapping/CategoryMappingCard';
import {
  ManageSubwalletCard,
} from "@/pages/Admin/HierarchyOfRetailers/CategoryMapping/ManageSubwalletCard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

enum RetailerDetailsTab {
  Overview = 'overview',
  Branding = 'branding',
  Organization = 'organization',
  Program = 'program',
  DataAndSync = 'data-and-sync',
}

const Loader = () => (
  <Container>
    <Stack alignItems='center'>
      <VSpacer size='14' />
      <CircularProgress />
    </Stack>
  </Container>
);

const RetailerDetailsOverviewPage = () => {
  const theme = useTheme();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [showAddMember, setShowAddMember] = useState(false);
  const [showViewMember, setShowViewMember] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isJobProcessing, setIsJobProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<RetailerDetailsTab>(RetailerDetailsTab.Branding);

  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backToAllRetailers = () => navigate(Routes.ADMIN_HIERARCHY_OF_RETAILERS);

  const onAddMember = () => setShowAddMember(!showAddMember);
  const onViewMember = () => setShowViewMember(!showViewMember);
  const onAddLocation = () => setShowLocation(!showLocation);
  const { debouncedSearch, search, setSearch } = useSearch();

  const { retailerMembers, isLoading } = useGetRetailerMembers(id, debouncedSearch);
  const { retailer } = useGetRetailerById(id);
  const agronomyProgramName = getMostRelevantProgram(
    retailer?.rewardsPrograms, RewardsProgramType.Agronomy,
  )?.name ?? 'Rewards & Loyalty Program';

  useQuery(
    [QueryKeys.GET_JOB_IS_PROCESSING, id],
    () => JobApi.getIsProcessing(id),
    {
      refetchInterval: (result, _) => {
        setIsJobProcessing(result ?? false);
        return result ? 5000 : false;
      },
    },
  );

  const { data: mappings } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS],
    () => RetailerApi.getCategoryMappings(id),
  );

  const { mutate: updateRetailer } = useMutation(
    (updates: RetailerEndpoint.Update.Request) => (
      RetailerApi.update(id, updates)
    ),
    {
      onSuccess: async (_, updates) => {
        if (!retailer?.description && updates.description) {
          openSnackbar('Description added');
        } else if (retailer?.description && updates.description) {
          openSnackbar('Description updated');
        } else if (retailer?.description && updates.description === null) {
          openSnackbar('Description deleted');
        }

        if (updates.image) {
          openSnackbar('Logo uploaded');
        } else if (updates.image === null) {
          openSnackbar('Logo deleted');
        }

        if (!retailer?.appIcon && updates.appIcon) {
          openSnackbar('App icon added');
        } else if (retailer?.appIcon && updates.appIcon) {
          openSnackbar('App icon updated');
        } else if (retailer?.appIcon && updates.appIcon === null) {
          openSnackbar('App icon deleted');
        }

        if (!retailer?.brandColor && updates.brandColor) {
          openSnackbar('Brand color added');
        } else if (retailer?.brandColor && updates.brandColor) {
          openSnackbar('Brand color updated');
        } else if (retailer?.brandColor && updates.brandColor === null) {
          openSnackbar('Brand color deleted');
        }

        if (!retailer?.websiteUrl && updates.websiteUrl) {
          openSnackbar('Website added');
        } else if (retailer?.websiteUrl && updates.websiteUrl) {
          openSnackbar('Website updated');
        } else if (retailer?.websiteUrl && updates.websiteUrl === null) {
          openSnackbar('Website deleted');
        }

        if (!retailer?.shortUrlName && updates.shortUrlName) {
          openSnackbar('Short URL added');
        } else if (retailer?.shortUrlName && updates.shortUrlName) {
          openSnackbar('Short URL updated');
        } else if (retailer?.shortUrlName && updates.shortUrlName === null) {
          openSnackbar('Short URL deleted');
        }

        if (!retailer?.historicalCutoffDate && updates.historicalCutoffDate) {
          openSnackbar('Historical cutoff added');
        } else if (retailer?.historicalCutoffDate && updates.historicalCutoffDate) {
          openSnackbar('Historical cutoff updated');
        }

        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
      },
      onError: (error: Error) => {
        openSnackbar(error.message || 'An error has occurred');
      },
    },
  );

  const { data: categories } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORIES, id],
    async () => {
      const result = await RetailerApi.listCategories(id);
      return result.filter(({ name }) => name);
    },
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Container maxWidth="lg">
      <Stack direction="row" justifyContent="space-between" py="24px">
        <Button
          onClick={backToAllRetailers}
          startIcon={<ArrowBackIcon />}
          testID="back-to-all-retailers"
          variant="text"
        >
          Back to all retailers
        </Button>
        {AppConfig.env.test && (
          <Button
            color="error"
            onClick={() => setShowDeleteModal(true)}
            startIcon={<DeleteOutline />}
            testID="delete-retailer-button"
          >
            Delete retailer
          </Button>
        )}
      </Stack>
      <RetailerDetailsCard />
      <VSpacer size="6" />
      <Box sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: TabCardRadius,
        overflow: 'hidden',
      }}
      >
        <Tabs
          onChange={(_, value) => setActiveTab(value)}
          sx={getTabBarSx(theme)}
          testID="retailer-details-tabs"
          value={activeTab}
        >
          <Tab label="Branding" value={RetailerDetailsTab.Branding} />
          <Tab label="Organization" value={RetailerDetailsTab.Organization} />
          <Tab label="Program" value={RetailerDetailsTab.Program} />
          <Tab label="Data & Sync" value={RetailerDetailsTab.DataAndSync} />
        </Tabs>
        <Box sx={{ backgroundColor: theme.palette.background.paper }}>
          {activeTab === RetailerDetailsTab.Overview && (
            <Box sx={{ p: '24px' }}>
              <EmptyState
                headingMessage="Overview"
                subline="Retailer performance and program metrics will appear here."
                testID="retailer-details-overview-tab-empty-state"
              />
            </Box>
          )}

          {activeTab === RetailerDetailsTab.Branding && !!retailer && (
            <BrandAssetInputs
              description={retailer.description}
              onChange={updateRetailer}
              retailer={retailer}
            />
          )}

          {activeTab === RetailerDetailsTab.Organization && (
            <Stack>
              <ProgramSectionHeader name={agronomyProgramName} type={RewardsProgramType.Agronomy} />
              <MembersCard
                members={retailerMembers}
                onAddMember={onAddMember}
                onViewMember={onViewMember}
                search={search}
                setSearch={setSearch}
                showAddMember={showAddMember}
                showViewMember={showViewMember}
              />
              <LocationCard
                isRetailerActive={retailer?.isActive ?? false}
                onAddLocation={onAddLocation}
                show={showLocation}
              />
              {!!retailer && (
                <AddRecipientsSection
                  retailerId={retailer.id}
                />
              )}
            </Stack>
          )}

          {activeTab === RetailerDetailsTab.Program && !!retailer && (
            <Stack>
              <ProgramSectionHeader name={agronomyProgramName} type={RewardsProgramType.Agronomy} />
              <Stack sx={{ p: '20px' }}>
                <CategoryMappingCard
                  isJobProcessing={isJobProcessing}
                  mappings={mappings}
                  programType={RewardsProgramType.Agronomy}
                  programs={retailer?.rewardsPrograms}
                  retailer={retailer}
                />
                <VSpacer size="6" />
                <RewardsProgramsCard
                  disabled={!categories?.length || retailer.isProgramCreationDisabled}
                  isJobProcessing={isJobProcessing}
                  programs={retailer?.rewardsPrograms}
                  retailer={retailer}
                />
                <VSpacer size="6" />
                <ManageSubwalletCard retailer={retailer} />
                <VSpacer size="4" />
                <HistoricalCutoffCard
                  historicalCutoffDate={retailer?.historicalCutoffDate}
                  onChange={(historicalCutoffDate) => (
                    updateRetailer({ historicalCutoffDate })
                  )}
                  retailerId={id}
                />
              </Stack>
              <EnergyProgramCard
                categoryMappingCard={(
                  <CategoryMappingCard
                    isJobProcessing={isJobProcessing}
                    mappings={mappings}
                    programType={RewardsProgramType.Energy}
                    programs={retailer?.rewardsPrograms}
                    retailer={retailer}
                  />
                )}
                context="program"
                programs={retailer?.rewardsPrograms}
                retailerId={retailer.id}
              />
            </Stack>
          )}

          {activeTab === RetailerDetailsTab.DataAndSync && (
            <Stack spacing={2} sx={{ p: '20px' }}>
              {!!retailer?.isErpIntegrationEnabled && !!mappings && (
                <IntegrationDetails
                  isJobProcessing={isJobProcessing}
                  retailerId={id}
                />
              )}
              {!!retailer && (
                <ErpImports retailer={retailer} />
              )}
            </Stack>
          )}
        </Box>
      </Box>
      <VSpacer size="6" />
      {showDeleteModal && (
        <ConfirmModal
          confirmButtonText="Yes, delete retailer"
          isLoading={isDeleting}
          message="Are you sure you want to delete the retailer?"
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={async () => {
            setIsDeleting(true);
            try {
              await RetailerApi.delete(id);
              await queryClient.invalidateQueries([QueryKeys.GET_RETAILERS]);
              openSnackbar('Retailer deleted successfully.');
              backToAllRetailers();
            } catch (error) {
              openSnackbar('Retailer delete unsuccessful');
            }
            setIsDeleting(false);
          }}
          open={showDeleteModal}
          testID="confirm-delete-retailer-modal"
          title="Delete retailer"
        />
      )}
    </Container>
  );
};

export default RetailerDetailsOverviewPage;
